import type { Icon } from '@phosphor-icons/react';
import {
  WalletIcon,
  ReceiptIcon,
  ShoppingCartIcon,
  PackageIcon,
  PercentIcon,
  TruckIcon,
  TagIcon,
  StorefrontIcon,
} from '@phosphor-icons/react';
import type { ErpLibraryModule, StepLibraryModule } from '@erp/workflows';
import { INTEGRATION_LIBRARY_MODULES } from '@erp/workflows';

export type { ErpLibraryModule };

export interface ErpModuleStyle {
  label: string;
  /** Shown below the module header when expanded (integration modules). */
  description?: string;
  Icon: Icon;
  accent: string;
  headerBg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  badge: string;
}

export const ERP_MODULE_STYLES: Record<ErpLibraryModule, ErpModuleStyle> = {
  AccountsPayable: {
    label: 'Accounts Payable',
    Icon: WalletIcon,
    accent: 'bg-emerald-500',
    headerBg:
      'bg-gradient-to-r from-emerald-50/90 to-transparent dark:from-emerald-950/50 dark:to-transparent',
    border: 'border-emerald-200/80 dark:border-emerald-800/60',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconColor: 'text-emerald-700 dark:text-emerald-300',
    badge:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  },
  AccountsReceivable: {
    label: 'Accounts Receivable',
    Icon: ReceiptIcon,
    accent: 'bg-sky-500',
    headerBg:
      'bg-gradient-to-r from-sky-50/90 to-transparent dark:from-sky-950/50 dark:to-transparent',
    border: 'border-sky-200/80 dark:border-sky-800/60',
    iconBg: 'bg-sky-100 dark:bg-sky-900/50',
    iconColor: 'text-sky-700 dark:text-sky-300',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200',
  },
  OrderManagement: {
    label: 'Order Management',
    Icon: ShoppingCartIcon,
    accent: 'bg-violet-500',
    headerBg:
      'bg-gradient-to-r from-violet-50/90 to-transparent dark:from-violet-950/50 dark:to-transparent',
    border: 'border-violet-200/80 dark:border-violet-800/60',
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconColor: 'text-violet-700 dark:text-violet-300',
    badge:
      'bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-200',
  },
  Procurement: {
    label: 'Procurement',
    Icon: PackageIcon,
    accent: 'bg-amber-500',
    headerBg:
      'bg-gradient-to-r from-amber-50/90 to-transparent dark:from-amber-950/50 dark:to-transparent',
    border: 'border-amber-200/80 dark:border-amber-800/60',
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconColor: 'text-amber-700 dark:text-amber-300',
    badge:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  },
  Rebates: {
    label: 'Rebates',
    Icon: PercentIcon,
    accent: 'bg-rose-500',
    headerBg:
      'bg-gradient-to-r from-rose-50/90 to-transparent dark:from-rose-950/50 dark:to-transparent',
    border: 'border-rose-200/80 dark:border-rose-800/60',
    iconBg: 'bg-rose-100 dark:bg-rose-900/50',
    iconColor: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200',
  },
  ShipAndDebit: {
    label: 'Ship & Debit',
    Icon: TruckIcon,
    accent: 'bg-indigo-500',
    headerBg:
      'bg-gradient-to-r from-indigo-50/90 to-transparent dark:from-indigo-950/50 dark:to-transparent',
    border: 'border-indigo-200/80 dark:border-indigo-800/60',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    iconColor: 'text-indigo-700 dark:text-indigo-300',
    badge:
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
  },
  ItemMaster: {
    label: 'Item Master',
    Icon: TagIcon,
    accent: 'bg-teal-500',
    headerBg:
      'bg-gradient-to-r from-teal-50/90 to-transparent dark:from-teal-950/50 dark:to-transparent',
    border: 'border-teal-200/80 dark:border-teal-800/60',
    iconBg: 'bg-teal-100 dark:bg-teal-900/50',
    iconColor: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200',
  },
  Pricing: {
    label: 'Pricing',
    Icon: TagIcon,
    accent: 'bg-teal-500',
    headerBg:
      'bg-gradient-to-r from-teal-50/90 to-transparent dark:from-teal-950/50 dark:to-transparent',
    border: 'border-teal-200/80 dark:border-teal-800/60',
    iconBg: 'bg-teal-100 dark:bg-teal-900/50',
    iconColor: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200',
  },
  Famous: {
    label: 'FAMOUS™',
    description: 'Famous Software®: ERP for fresh produce distributors.',
    Icon: StorefrontIcon,
    // Brand greens from famoussoftware.com (#41AD49 primary, #BBD9B1 accent)
    accent: 'bg-[#41AD49]',
    headerBg:
      'bg-gradient-to-r from-[#BBD9B1]/40 to-transparent dark:from-[#001100]/50 dark:to-transparent',
    border: 'border-[#41AD49]/25 dark:border-[#41AD49]/35',
    iconBg: 'bg-[#BBD9B1]/50 dark:bg-[#41AD49]/15',
    iconColor: 'text-[#2d8a34] dark:text-[#BBD9B1]',
    badge:
      'bg-[#BBD9B1]/60 text-[#1a4d1e] dark:bg-[#41AD49]/20 dark:text-[#BBD9B1]',
  },
};

const integrationModuleSet: ReadonlySet<ErpLibraryModule> = new Set(
  INTEGRATION_LIBRARY_MODULES,
);

/** ERP modules in display order (alphabetical by label), integrations excluded */
export const ERP_MODULE_ORDER = (
  Object.keys(ERP_MODULE_STYLES) as (keyof typeof ERP_MODULE_STYLES)[]
)
  .filter((module) => !integrationModuleSet.has(module))
  .sort((a, b) =>
    ERP_MODULE_STYLES[a].label.localeCompare(ERP_MODULE_STYLES[b].label),
  ) satisfies readonly ErpLibraryModule[];

/** Integration modules (own sub-section below ERP modules), alphabetical by label */
export const INTEGRATION_MODULE_ORDER = (
  Object.keys(ERP_MODULE_STYLES) as (keyof typeof ERP_MODULE_STYLES)[]
)
  .filter((module) => integrationModuleSet.has(module))
  .sort((a, b) =>
    ERP_MODULE_STYLES[a].label.localeCompare(ERP_MODULE_STYLES[b].label),
  ) satisfies readonly ErpLibraryModule[];

export function isErpLibraryModule(
  module: StepLibraryModule,
): module is ErpLibraryModule {
  return module !== 'Platform';
}
